import type { FleetGateway } from '../../core/bridge/connect-client';

/*
 * NotificationManager — the app's bell-icon notification store.
 *
 * A tiny, GENERAL EventTarget store (the same manager-proxy shape as
 * ControllerManager/FleetManager): hold a list of notifications, expose
 * add/remove/markRead, emit `change`. Today the only producer is the claimable-
 * gateway sync (app-shell reconciles it off the FleetManager), but the shape is
 * deliberately open so future kinds (model errors, OTA, …) plug in by adding a
 * `kind` + a `data` payload — no new manager.
 *
 * Cardinal Rule: this owns NO network IO. It is a pure in-memory store fed by
 * other managers; components read `list`/`pendingCount` and subscribe to `change`.
 */
export type NotificationKind = 'claimable';

export interface AppNotification {
  /** Stable id — re-adding the same id UPDATES in place (idempotent sync). For a
   *  claimable gateway it's `claim:<fingerprint>`, so repeated discovery polls
   *  never duplicate the row. */
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  /** Monotonic ins-order key for stable newest-first sorting (we can't call
   *  Date.now in some sandboxes; a counter is enough and never collides). */
  seq: number;
  /** Kind-specific payload. For `claimable` it's the discovered FleetGateway. */
  data?: FleetGateway;
}

export class NotificationManager extends EventTarget {
  // Hard cap so the store can't grow without bound — today's only producer
  // (claimable boxes) is self-reconciled, but the store is deliberately open to
  // future feed-style kinds (model errors, OTA), and an uncapped Map is a latent
  // O(n) leak the moment one lands. Generous; oldest-evicted on overflow.
  private static readonly MAX = 200;
  private items = new Map<string, AppNotification>();
  private seqCounter = 0;

  /** All notifications, newest first. */
  get list(): AppNotification[] {
    return [...this.items.values()].sort((a, b) => b.seq - a.seq);
  }

  /** Rows outstanding — drives the bell badge. Not "unread": notifications are
   *  shared, so the dot follows what is still THERE and goes out when a row is
   *  cleared (for everyone), never because this client opened the panel. */
  get pendingCount(): number {
    return this.items.size;
  }

  /**
   * Insert or update a notification by id. A NEW id appears in the list; an EXISTING
   * id keeps its read state + seq (so a re-sync of the same claimable box doesn't
   * re-mark-unread or reorder it) and just refreshes title/body/data.
   */
  upsert(n: Omit<AppNotification, 'read' | 'seq'>): void {
    const existing = this.items.get(n.id);
    if (existing) {
      existing.title = n.title;
      existing.body = n.body;
      existing.data = n.data;
    } else {
      this.items.set(n.id, { ...n, seq: this.seqCounter++ });
      if (this.items.size > NotificationManager.MAX) {
        // Evict the oldest (lowest seq) — keeps the store bounded under any feed.
        let oldestId: string | undefined;
        let oldestSeq = Infinity;
        for (const it of this.items.values()) {
          if (it.seq < oldestSeq) {
            oldestSeq = it.seq;
            oldestId = it.id;
          }
        }
        if (oldestId && oldestId !== n.id) this.items.delete(oldestId);
      }
    }
    this.dispatchEvent(new CustomEvent('change'));
  }

  /** Remove a notification (e.g. its claimable box got claimed or left the LAN). */
  remove(id: string): void {
    if (this.items.delete(id)) this.dispatchEvent(new CustomEvent('change'));
  }


}
